"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import isEqual from "lodash.isequal";
import isNull from "lodash.isnull";
import omitBy from "lodash.omitby";
import isEmpty from "lodash.isempty";

import {
  SetDefaultViewOptions,
  useEmitSetDefaultViewEvent,
  useRouterParams,
} from "../../../index";
import { useNotification } from "@zapier/utils";
import { useDefaultViewStorage } from "./useDefaultViewStorage";
import { DefaultViewSelectionFilters } from "./types";

interface Props {
  children: React.ReactNode;
}

interface DefaultViewContextType {
  handleResetDefaultView: () => void;
  handleSaveDefaultView: () => void;
  isButtonsDisabled: boolean;
  setDefaultView: () => void;
}
interface DefaultViewSelection {
  "app/assets/zaps"?: DefaultViewSelectionFilters;
  "app/assets/zaps/folders"?: DefaultViewSelectionFilters;
  "app/assets/folders"?: DefaultViewSelectionFilters;
  "app/assets/tables"?: DefaultViewSelectionFilters;
  "app/assets/forms"?: DefaultViewSelectionFilters;
  "app/assets/chatbots"?: DefaultViewSelectionFilters;
  "app/assets/canvases"?: DefaultViewSelectionFilters;
  "app/assets/agents"?: DefaultViewSelectionFilters;
  "app/assets/next-gen-zaps"?: DefaultViewSelectionFilters;
}

const ALL_FILTER_KEYS: (keyof DefaultViewSelectionFilters)[] = [
  "app",
  "apps",
  "appsOperator",
  "connection",
  "connections",
  "folderType",
  "lastRun",
  "order",
  "owner",
  "owners",
  "ownersOperator",
  "search",
  "shared",
  "status",
  "page",
  "virtual",
];

const defaultViewContext = {
  handleResetDefaultView: () => {},
  handleSaveDefaultView: () => {},
  isButtonsDisabled: false,
  setDefaultView: () => {},
};

const DefaultViewContext =
  createContext<DefaultViewContextType>(defaultViewContext);

export function useDefaultView(): DefaultViewContextType {
  return useContext(DefaultViewContext);
}

export function DefaultViewProvider(props: Props) {
  const { emit } = useEmitSetDefaultViewEvent();
  const { createNotification } = useNotification();
  const pathname = usePathname() as keyof DefaultViewSelection;

  const {
    app,
    apps,
    appsOperator,
    connection,
    connections,
    folderType,
    fullUrl,
    lastRun,
    order,
    owner,
    owners,
    ownersOperator,
    search,
    setParams,
    shared,
    status,
    virtual,
    clearAllParams,
  } = useRouterParams();

  const {
    savedFiltersForPathname,
    saveFilters,
    loadFiltersForPathname,
    hasStoredView,
    isReady,
  } = useDefaultViewStorage();

  const [lastAppliedPathname, setLastAppliedPathname] = useState<string>("");

  const currentFilters = useMemo(
    () =>
      omitBy(
        {
          app,
          apps,
          appsOperator,
          connection,
          connections,
          folderType,
          lastRun,
          order,
          owner,
          owners,
          ownersOperator,
          search,
          shared,
          status,
          virtual,
        },
        isNull,
      ),
    [
      app,
      apps,
      appsOperator,
      connection,
      connections,
      folderType,
      lastRun,
      order,
      owner,
      owners,
      ownersOperator,
      search,
      shared,
      status,
      virtual,
    ],
  );

  const isButtonsDisabled = useMemo(() => {
    if (
      isEqual(currentFilters, savedFiltersForPathname) ||
      (!hasStoredView(pathname) && isEmpty(currentFilters))
    ) {
      return true;
    }
    return false;
  }, [currentFilters, savedFiltersForPathname, hasStoredView, pathname]);

  const applyDefaultView = useCallback(() => {
    setLastAppliedPathname(pathname);

    if (!savedFiltersForPathname) {
      clearAllParams();
      return;
    }

    // Only omit parameters that aren't in the saved filters
    const parametersToOmit = ALL_FILTER_KEYS.filter(
      (key) => savedFiltersForPathname[key] === undefined,
    );

    setParams(
      savedFiltersForPathname as Record<string, string | number>,
      [...parametersToOmit, "pathname"],
      pathname,
      true,
    );
  }, [savedFiltersForPathname, pathname, setParams, clearAllParams]);

  const handleSaveDefaultView = useCallback(() => {
    if (!pathname) {
      createNotification({
        message: "Default view cannot be saved",
        type: "error",
      });
      return;
    }

    saveFilters(pathname, currentFilters);
    setLastAppliedPathname(pathname);

    emit({
      optionSelected: SetDefaultViewOptions.Set,
      interactionRawText: fullUrl,
    });

    createNotification({
      message: "Default view saved",
      type: "success",
    });
  }, [
    pathname,
    currentFilters,
    fullUrl,
    emit,
    createNotification,
    saveFilters,
  ]);

  // Both reset and set operations use the same apply logic
  const handleResetDefaultView = applyDefaultView;
  const setDefaultView = applyDefaultView;

  // Load and apply saved default view when pathname changes
  useEffect(() => {
    if (!isReady) {
      return;
    }

    // Only apply if pathname has changed
    if (pathname === lastAppliedPathname) {
      return;
    }

    const savedFilters = loadFiltersForPathname(pathname);
    setLastAppliedPathname(pathname);

    // Only apply saved filters when no URL params are already present.
    // Existing params (deep links, shared links) take precedence over the
    // saved view. Use window.location since useSearchParams can lag during hydration.
    if (savedFilters && globalThis.window?.location.search === "") {
      setParams(
        savedFilters as Record<string, string | number>,
        ["pathname"],
        pathname,
        true,
      );
    }
  }, [
    pathname,
    isReady,
    lastAppliedPathname,
    loadFiltersForPathname,
    setParams,
  ]);

  const value = {
    handleResetDefaultView,
    handleSaveDefaultView,
    isButtonsDisabled,
    setDefaultView,
  };

  return (
    <DefaultViewContext.Provider value={value}>
      {props.children}
    </DefaultViewContext.Provider>
  );
}
